import React, { useState } from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useForm } from "react-hook-form";
import { useAuth } from "../util/auth";
import { Dialog, DialogTitle, DialogContentText, DialogContent, DialogActions } from "@material-ui/core";
import { deleteAccount } from "../util/apiHelpers";
import { firebaseApp } from "../util/firebase";
import { getAuth, signOut } from "firebase/auth";

function SettingsSecurity(props) {
  const auth = useAuth();
  const [pending, setPending] = useState(false);

  // DELETE account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [reauthRequired, setReauthRequired] = useState(false);
  const [reauthPassword, setReauthPassword] = useState("");

  const {
    register,
    handleSubmit,
    errors,
    reset,
    getValues,
    formState
  } = useForm({ mode: "onChange" });

  const cancelDeleteDialog = () => setDeleteDialogOpen(false);

  // ---------------- PASSWORD CHANGE ----------------
  const onSubmit = (data) => {
    setPending(true);
    auth.updatePassword(data.pass)
      .then(() => {
        reset();
        props.onStatus({ type: "success", message: "Your password has been updated" });
      })
      .catch((error) => {
        if (error.code === "auth/requires-recent-login") {
          props.onStatus({
            type: "requires-recent-login",
            callback: () => onSubmit({ pass: data.pass }),
          });
        } else {
          props.onStatus({ type: "error", message: error.message });
        }
      })
      .finally(() => setPending(false));
  };

  // ---------------- DELETE ACCOUNT ----------------
  const handleDelete = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
  
    let isMounted = true; // flag to avoid state updates after unmount
  
    try {
      await deleteAccount(); // backend deletes user + data + stripe subscription
  
      const authInstance = getAuth(firebaseApp);
      await signOut(authInstance); // sign out directly
  
      if (isMounted) window.location.href = "/auth/signup";
    } catch (error) {
      console.error("Failed to delete account:", error);
      if (isMounted) setDeleteError(error.message || "Unexpected error deleting account.");
    } finally {
      if (isMounted) {
        setDeleteLoading(false);
        setDeleteDialogOpen(false);
      }
    }
  };

  return (
    <>
      <h1>CHANGE PASSWORD</h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              type="password"
              label="Password"
              name="pass"
              placeholder="Password"
              error={errors.pass ? true : false}
              helperText={errors.pass && errors.pass.message}
              fullWidth
              inputRef={register({
                required: "Password is required",
                pattern: {
                  value: /^(?=.*[0-9])(?=.*[^A-Za-z0-9])/,
                  message: "Password must include at least one number and one symbol",
                },
                minLength: { value: 8, message: "Password must be at least 8 characters" },
              })}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              variant="outlined"
              type="password"
              label="Confirm New Password"
              name="confirmPass"
              placeholder="Confirm Password"
              error={errors.confirmPass ? true : false}
              helperText={errors.confirmPass && errors.confirmPass.message}
              fullWidth
              inputRef={register({
                required: "Please enter your new password again",
                validate: (value) => value === getValues().pass || "This doesn't match your password",
              })}
            />
          </Grid>

          <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                disabled={pending || !formState.isValid}
                fullWidth
              >
                {!pending ? "Save" : <CircularProgress size={28} />}
              </Button>
          </Grid>
        </Grid>
      </form>

      <br /><br /><br />

      <h1>DELETE ACCOUNT</h1>
      <p>
        <span style={{ color: 'red' }}>Warning! </span>
        This action will permanently delete all location information associated with your account. If you would just like to stop payment and keep read-only access to your dashboard, you can cancel your subscription under the billing section.
      </p>
      <Button
        variant="outlined"
        color="secondary"
        size="large"
        onClick={() => setDeleteDialogOpen(true)}
        fullWidth
      >
        DELETE ACCOUNT
      </Button>

      {/* DELETE DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteDialog}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title">
          Are you sure you want to delete your account?
        </DialogTitle>
        <DialogContent>
          {reauthRequired ? (
            <>
              <TextField
                type="password"
                label="Enter password to confirm"
                value={reauthPassword}
                onChange={(e) => setReauthPassword(e.target.value)}
                fullWidth
              />
            </>
          ) : (
            <DialogContentText>
              This will delete all information associated with your account and you will no longer have access to showNAV dashboards.
            </DialogContentText>
          )}
          {deleteError && <p style={{ color: "red" }}>Sorry, there was an error deleting your account. Please contact showNAV support.</p>}

        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteDialog} color="primary">CANCEL</Button>
          <Button
            onClick={handleDelete}
            color="secondary"
            variant="outlined"
            autoFocus
            disabled={deleteLoading || deleteError}
          >
            {deleteLoading ? <CircularProgress size={24} /> : "DELETE MY ACCOUNT"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SettingsSecurity;
